import React, { useEffect, useMemo, useState } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Typography,
	Box,
	CircularProgress,
	Autocomplete,
	createFilterOptions,
} from '@mui/material';
import { Cancel, Save } from '@mui/icons-material';
import { apiClient } from 'utils/ApiClient';
import { Logger } from 'utils/Logger';

export interface UserNotificationRecord {
	_id: string;
	name: string;
	eventType: string;
	message: string;
	channel: 'email' | 'gsm' | 'whatsapp';
	status: 'active' | 'inactive';
	createdBy?: string;
	updatedBy?: string;
	createdAt?: string;
	updatedAt?: string;
}

interface EventTypeOption {
	value: string;
	description: string;
}

interface Props {
	open: boolean;
	onClose: () => void;
	onSave: () => void;
	notification: UserNotificationRecord | null;
}

const CHANNEL_OPTIONS: { value: UserNotificationRecord['channel']; label: string }[] = [
	{ value: 'email', label: 'Email' },
	{ value: 'gsm', label: 'GSM Text' },
	{ value: 'whatsapp', label: 'WhatsApp' },
];

const MESSAGE_VARIABLES_HELP =
	'You can use variables in the message: {{auditEventName}}, {{auditEventDescription}}, {{user.firstName}}, {{user.lastName}}';

const MESSAGE_TEMPLATE =
	'Dear {{user.firstName}} {{user.lastName}},\n{{auditEventDescription}},\nThanks!';

const EMPTY_FORM = {
	name: '',
	eventType: '',
	message: '',
	channel: 'email' as UserNotificationRecord['channel'],
	status: 'active' as UserNotificationRecord['status'],
};

const filterEventTypeOptions = createFilterOptions<EventTypeOption>({
	stringify: (option) => `${option.value} ${option.description}`,
});

export const NotificationDialog: React.FC<Props> = ({ open, onClose, onSave, notification }) => {
	const isEditing = Boolean(notification?._id);
	const [form, setForm] = useState(EMPTY_FORM);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [saving, setSaving] = useState(false);
	const [eventTypes, setEventTypes] = useState<EventTypeOption[]>([]);
	const [loadingEventTypes, setLoadingEventTypes] = useState(false);

	useEffect(() => {
		if (!open) return;
		if (notification) {
			setForm({
				name: notification.name || '',
				eventType: notification.eventType || '',
				message: notification.message || '',
				channel: notification.channel || 'email',
				status: notification.status || 'active',
			});
		} else {
			setForm(EMPTY_FORM);
		}
		setErrors({});
	}, [open, notification]);

	useEffect(() => {
		if (!open) return;
		const loadEventTypes = async () => {
			setLoadingEventTypes(true);
			try {
				const response = await apiClient.get('/user-notifications/event-types');
				setEventTypes(Array.isArray(response.data) ? response.data : []);
			} catch (error) {
				Logger.error('NotificationDialog', `Failed to load event types: ${error}`);
				setEventTypes([]);
			} finally {
				setLoadingEventTypes(false);
			}
		};
		loadEventTypes();
	}, [open]);

	const handleEventTypeChange = (value: string) => {
		setForm((prev) => ({
			...prev,
			eventType: value,
			// Only auto-fill when Message is empty so manual edits are preserved
			message: !prev.message.trim() ? MESSAGE_TEMPLATE : prev.message,
		}));
	};

	const selectedEventType = useMemo(
		() => eventTypes.find((et) => et.value === form.eventType) || null,
		[eventTypes, form.eventType],
	);

	const validate = (): boolean => {
		const next: Record<string, string> = {};
		if (!form.name.trim()) next.name = 'Name is required';
		if (!form.eventType.trim()) next.eventType = 'Event Type is required';
		if (!form.message.trim()) next.message = 'Message is required';
		if (!form.channel) next.channel = 'Notification Channel is required';
		setErrors(next);
		return Object.keys(next).length === 0;
	};

	const handleSave = async () => {
		if (!validate()) return;
		setSaving(true);
		try {
			const payload = {
				name: form.name.trim(),
				eventType: form.eventType.trim(),
				message: form.message,
				channel: form.channel,
				status: form.status,
			};
			if (isEditing && notification?._id) {
				await apiClient.put(`/user-notifications/${notification._id}`, payload);
			} else {
				await apiClient.post('/user-notifications/new', payload);
			}
			onSave();
		} catch (error) {
			Logger.error('NotificationDialog', `Failed to save notification: ${error}`);
			setErrors({ form: error instanceof Error ? error.message : 'Failed to save notification' });
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>{isEditing ? 'Edit Notification' : 'New Notification'}</DialogTitle>
			<DialogContent>
				<Box display="flex" flexDirection="column" gap={2} pt={1}>
					{errors.form && (
						<Typography color="error" variant="body2">{errors.form}</Typography>
					)}
					<TextField
						label="Name"
						value={form.name}
						onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
						error={Boolean(errors.name)}
						helperText={errors.name}
						fullWidth
						required
						size="small"
					/>
					<Autocomplete<EventTypeOption, false, false, false>
						options={eventTypes}
						loading={loadingEventTypes}
						disabled={loadingEventTypes}
						filterOptions={filterEventTypeOptions}
						getOptionLabel={(option) => option.value}
						isOptionEqualToValue={(a, b) => a.value === b.value}
						value={selectedEventType}
						onChange={(_, newValue) => {
							handleEventTypeChange(newValue?.value ?? '');
						}}
						renderOption={(props, option) => (
							<li {...props} key={option.value}>
								<Box>
									<Typography variant="body2">{option.value}</Typography>
									{option.description && (
										<Typography variant="caption" color="text.secondary" display="block">
											{option.description}
										</Typography>
									)}
								</Box>
							</li>
						)}
						ListboxProps={{ style: { maxHeight: 280 } }}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Event Type"
								required
								size="small"
								placeholder="Type to filter event types"
								error={Boolean(errors.eventType)}
								helperText={
									errors.eventType
									|| (form.eventType
										? (selectedEventType?.description || '')
										: 'Type to search the event type list')
								}
								InputProps={{
									...params.InputProps,
									endAdornment: (
										<>
											{loadingEventTypes ? <CircularProgress color="inherit" size={18} /> : null}
											{params.InputProps.endAdornment}
										</>
									),
								}}
							/>
						)}
						fullWidth
					/>
					<TextField
						label="Message"
						value={form.message}
						onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))}
						error={Boolean(errors.message)}
						helperText={errors.message || MESSAGE_VARIABLES_HELP}
						fullWidth
						required
						multiline
						minRows={4}
						size="small"
					/>
					<FormControl fullWidth size="small" error={Boolean(errors.channel)} required>
						<InputLabel>Notification Channel</InputLabel>
						<Select
							label="Notification Channel"
							value={form.channel}
							onChange={(e) =>
								setForm((prev) => ({
									...prev,
									channel: e.target.value as UserNotificationRecord['channel'],
								}))
							}
						>
							{CHANNEL_OPTIONS.map((opt) => (
								<MenuItem key={opt.value} value={opt.value}>
									{opt.label}
								</MenuItem>
							))}
						</Select>
						{errors.channel && (
							<Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
								{errors.channel}
							</Typography>
						)}
					</FormControl>
					{isEditing && (
						<FormControl fullWidth size="small">
							<InputLabel>Status</InputLabel>
							<Select
								label="Status"
								value={form.status}
								onChange={(e) =>
									setForm((prev) => ({
										...prev,
										status: e.target.value as UserNotificationRecord['status'],
									}))
								}
							>
								<MenuItem value="active">Active</MenuItem>
								<MenuItem value="inactive">Inactive</MenuItem>
							</Select>
						</FormControl>
					)}
				</Box>
			</DialogContent>
			<DialogActions>
				<Button
					variant="outlined"
					color="secondary"
					onClick={onClose}
					disabled={saving}
					startIcon={<Cancel />}
				>
					Cancel
				</Button>
				<Button
					onClick={handleSave}
					startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
					variant="contained"
					color="success"
					disabled={saving || loadingEventTypes}
				>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default NotificationDialog;
